import { useEffect, useState } from 'react';
import Seo from '@/components/Seo';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { UploadDropzone } from '@/components/tools/UploadDropzone';
import { ToolLongForm } from '@/components/tools/ToolLongForm';
import { ToolFAQ } from '@/components/tools/ToolFAQ';
import { AdSlot } from '@/components/ads/AdSlot';
import {
    getImageDimensions,
    resizeImageApi,
    absoluteApiUrl,
} from '@/lib/api';
import { downloadFromUrl, humanBytes } from '@/lib/download';
import { AlertCircle, CheckCircle2, Download, Loader2, RotateCcw, Ruler, ShieldCheck } from 'lucide-react';
import { TOOL_PAGE } from '@/constants/testIds';
import { toast } from 'sonner';

const FAQS = [
    { q: 'Which formats are supported?', a: 'JPG, PNG, WebP, GIF, and BMP. The output keeps the same format as the input.' },
    { q: 'Does the aspect ratio stay locked?', a: 'By default, yes. Toggle “Keep aspect ratio” off if you want to stretch to exact width and height.' },
    { q: 'What’s the difference between Contain and Cover?', a: 'Contain fits your image inside the target box (one axis may be smaller). Cover fills the box completely, cropping any overflow from the center.' },
    { q: 'What’s the maximum size?', a: 'Up to 25 MB per upload, and target dimensions up to 10000×10000.' },
    { q: 'Do you re-compress the image?', a: 'JPG and WebP outputs use a high default quality (90). PNG remains lossless. If you want smaller files, run the result through our Image Compressor.' },
];

const HOW = [
    'Upload a JPG, PNG, WebP, GIF, or BMP image. We’ll read its exact pixel dimensions and pre-fill the target width and height. From there, you decide the new size — you can type any width or height, and if you keep the aspect ratio locked (the default), the other value updates automatically.',
    'When both a width and a height are provided and aspect ratio is locked, you can pick a fit mode. “Contain” shrinks the image so it fits inside your target box with no cropping, so it may end up slightly smaller on one axis. “Cover” fills the box completely and crops the overflow from the center. “Stretch” only applies when you turn off the aspect-ratio lock — it forces the image to your exact width and height.',
    'Behind the scenes, UtilityTool uses Pillow’s LANCZOS resampling algorithm — the same high-quality filter used by professional image tools. That keeps edges sharp and colors natural even on aggressive downscales, and avoids the muddy “JPEG-compression-then-resize” artefacts you often see with cheap online resizers.',
    'When resizing is complete you get a download button and a note about the exact output dimensions. As with all our tools, your image is deleted from our servers the moment we’ve delivered your download.',
];
const BENEFITS = [
    'Right-sized images matter more than most people realise. If you upload a 4000 × 3000 photo where the layout only shows 800 × 600, every visitor still has to download the full-resolution file. Multiply that by hundreds of images and thousands of visits, and you’re shipping megabytes of pixels no one ever sees. Resizing to the actual displayed dimensions is one of the highest-leverage performance improvements you can make.',
    'For blog authors, social media, and e-commerce, each platform has its own “happy path” dimensions. LinkedIn cover images want 1584 × 396. YouTube thumbnails want 1280 × 720. Instagram posts prefer 1080 × 1080. Resizing beforehand means your platform doesn’t re-compress your image with its own aggressive settings, which is why bulk-resized uploads always look sharper than “auto-fit” ones.',
    'For printing and archival work, precise dimensions also help. If your print layout expects 300 DPI at 4 × 6 inches, that’s 1200 × 1800 pixels. Feeding the printer exactly that avoids upscaling artefacts and gives predictable results.',
];
const TIPS = [
    'Keep the aspect ratio locked unless you explicitly need a stretched result.',
    'Use “Cover” when preparing profile pictures or thumbnails that must fully fill a square.',
    'After resizing, run the result through the Image Compressor for maximum web performance.',
];

export default function ResizeImagePage() {
    const [file, setFile] = useState(null);
    const [origDims, setOrigDims] = useState(null); // {width, height}
    const [width, setWidth] = useState('');
    const [height, setHeight] = useState('');
    const [keepAspect, setKeepAspect] = useState(true);
    const [fitMode, setFitMode] = useState('contain');
    const [status, setStatus] = useState('idle');
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);

    const reset = () => {
        setFile(null);
        setOrigDims(null);
        setWidth('');
        setHeight('');
        setKeepAspect(true);
        setFitMode('contain');
        setProgress(0);
        setStatus('idle');
        setError('');
        setResult(null);
    };

    const onFile = async (f) => {
        setFile(f);
        setStatus('idle');
        setError('');
        setResult(null);
        setOrigDims(null);
        try {
            const dims = await getImageDimensions(f);
            setOrigDims(dims);
            // Pre-fill with source dims so users can just shave a bit off
            setWidth(String(dims.width));
            setHeight(String(dims.height));
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Could not read this image.';
            setError(msg);
            setStatus('error');
        }
    };

    // Keep aspect ratio when user types
    const onWidthChange = (v) => {
        setWidth(v);
        if (keepAspect && origDims && v !== '' && !isNaN(Number(v))) {
            const w = Number(v);
            const h = Math.max(1, Math.round((origDims.height / origDims.width) * w));
            setHeight(String(h));
        }
    };
    const onHeightChange = (v) => {
        setHeight(v);
        if (keepAspect && origDims && v !== '' && !isNaN(Number(v))) {
            const h = Number(v);
            const w = Math.max(1, Math.round((origDims.width / origDims.height) * h));
            setWidth(String(w));
        }
    };

    const run = async () => {
        if (!file) return;
        const w = width ? Number(width) : null;
        const h = height ? Number(height) : null;
        if (!w && !h) {
            setError('Enter at least a width or a height.');
            setStatus('error');
            return;
        }
        setStatus('uploading');
        setError('');
        setProgress(0);
        try {
            const envelope = await resizeImageApi(
                file,
                { width: w, height: h, keepAspect, fitMode },
                setProgress,
            );
            setResult(envelope);
            setStatus('done');
            toast.success(`Resized to ${envelope.width}\u00d7${envelope.height}.`);
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || 'Something went wrong.';
            setError(msg);
            setStatus('error');
            toast.error(msg);
        }
    };

    const structured = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'UtilityTool — Resize Image',
        applicationCategory: 'Utility',
        operatingSystem: 'Any',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    };

    const bothDimsSet = width && height;

    return (
        <>
            <Seo
                title="Resize Image"
                description="Resize JPG, PNG, WebP, GIF or BMP images to exact dimensions with high-quality LANCZOS resampling. Aspect-ratio aware, free, no signup."
                canonical="/tools/resize-image"
                structuredData={structured}
            />
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-14">
                <Breadcrumb items={[{ label: 'Tools', to: '/tools' }, { label: 'Resize Image' }]} />
                <div className="mt-4 flex items-start gap-4">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--primary))]">
                        <Ruler className="w-6 h-6" />
                    </span>
                    <div>
                        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
                            Resize Image
                        </h1>
                        <p className="mt-2 text-slate-600 max-w-2xl">
                            Change the dimensions of an image while keeping the aspect ratio.
                            Uses high-quality LANCZOS resampling.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">Aspect-aware</Badge>
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">LANCZOS quality</Badge>
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">Up to 25 MB</Badge>
                        </div>
                    </div>
                </div>

                <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-6 lg:gap-8">
                    <Card className="p-5 sm:p-6 border-border/80">
                        <UploadDropzone
                            accept=".jpg,.jpeg,.png,.webp,.gif,.bmp,image/*"
                            file={file}
                            onFile={onFile}
                            onClear={reset}
                            disabled={status === 'uploading'}
                            hint="Accepted formats: .jpg, .jpeg, .png, .webp, .gif, .bmp · Max 25 MB"
                        />

                        {origDims ? (
                            <p className="mt-3 text-xs text-slate-500">
                                Original dimensions:{' '}
                                <span className="font-medium text-slate-700 tabular-nums">
                                    {origDims.width} × {origDims.height} px
                                </span>
                            </p>
                        ) : null}

                        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="rsz-width">Width (px)</Label>
                                <Input
                                    id="rsz-width"
                                    data-testid="tool-resize-width"
                                    type="number"
                                    min={1}
                                    max={10000}
                                    value={width}
                                    onChange={(e) => onWidthChange(e.target.value)}
                                    placeholder="e.g. 1280"
                                    className="mt-1.5"
                                    disabled={!file}
                                />
                            </div>
                            <div>
                                <Label htmlFor="rsz-height">Height (px)</Label>
                                <Input
                                    id="rsz-height"
                                    data-testid="tool-resize-height"
                                    type="number"
                                    min={1}
                                    max={10000}
                                    value={height}
                                    onChange={(e) => onHeightChange(e.target.value)}
                                    placeholder="e.g. 720"
                                    className="mt-1.5"
                                    disabled={!file}
                                />
                            </div>
                        </div>

                        <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                            <div className="flex items-center gap-3">
                                <Switch
                                    id="rsz-keep"
                                    data-testid="tool-resize-keep-aspect"
                                    checked={keepAspect}
                                    onCheckedChange={setKeepAspect}
                                />
                                <Label htmlFor="rsz-keep" className="cursor-pointer">
                                    Keep aspect ratio
                                </Label>
                            </div>
                            {keepAspect && bothDimsSet ? (
                                <div className="flex items-center gap-2">
                                    <Label className="text-slate-600">Fit mode</Label>
                                    <Select value={fitMode} onValueChange={setFitMode}>
                                        <SelectTrigger className="w-[160px]" data-testid="tool-resize-fit-mode">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="contain">Contain (fit)</SelectItem>
                                            <SelectItem value="cover">Cover (crop)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            ) : null}
                        </div>

                        {status === 'uploading' ? (
                            <div className="mt-6">
                                <div className="flex items-center justify-between text-sm mb-2">
                                    <span className="font-medium text-slate-800">Resizing…</span>
                                    <span className="text-slate-500">{progress}%</span>
                                </div>
                                <Progress value={progress} className="h-2" />
                            </div>
                        ) : null}

                        {status === 'error' && error ? (
                            <Alert variant="destructive" className="mt-6" data-testid={TOOL_PAGE.errorAlert}>
                                <AlertCircle className="w-4 h-4" />
                                <AlertTitle>We couldn’t resize this image</AlertTitle>
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        ) : null}

                        {status === 'done' && result ? (
                            <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid={TOOL_PAGE.resultCard}>
                                <div className="flex items-center gap-2 text-emerald-700">
                                    <CheckCircle2 className="w-4 h-4" />
                                    <span className="text-sm font-medium">Resize complete</span>
                                </div>
                                <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                                    <MetaCell label="Width" value={`${result.width || 0} px`} />
                                    <MetaCell label="Height" value={`${result.height || 0} px`} />
                                    <MetaCell label="File size" value={humanBytes(result.size_bytes)} />
                                </div>
                                <div className="mt-4 flex items-center justify-between gap-3">
                                    <p className="text-xs text-slate-500 truncate">{result.filename}</p>
                                    <Button
                                        data-testid={TOOL_PAGE.downloadButton}
                                        onClick={() => downloadFromUrl(absoluteApiUrl(result.download_url), result.filename)}
                                        className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--link-hover))] text-white"
                                    >
                                        <Download className="w-4 h-4 mr-2" /> Download
                                    </Button>
                                </div>
                            </div>
                        ) : null}

                        <div className="mt-6 flex flex-col sm:flex-row gap-3">
                            <Button
                                onClick={run}
                                disabled={!file || status === 'uploading'}
                                data-testid={TOOL_PAGE.runButton}
                                className="h-11 bg-slate-900 hover:bg-slate-800 text-white"
                            >
                                {status === 'uploading' ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Resizing…
                                    </>
                                ) : (
                                    'Resize image'
                                )}
                            </Button>
                            <Button variant="secondary" onClick={reset} className="h-11">
                                <RotateCcw className="w-4 h-4 mr-2" /> Reset
                            </Button>
                        </div>
                    </Card>

                    <aside className="space-y-4">
                        <Card className="p-5 border-border/80 bg-[hsl(var(--surface-2))]">
                            <div className="flex items-center gap-2 text-slate-800">
                                <ShieldCheck className="w-4 h-4 text-[hsl(var(--primary))]" />
                                <span className="text-sm font-semibold">Your image is private</span>
                            </div>
                            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                                Files are processed in an isolated worker and deleted from
                                disk immediately after resizing.
                            </p>
                        </Card>
                        <Card className="p-5 border-border/80">
                            <p className="text-sm font-semibold text-slate-900">Also useful</p>
                            <ul className="mt-2 space-y-1.5 text-sm">
                                <li><a href="/tools/image-compressor" className="text-[hsl(var(--primary))] hover:underline">Image Compressor</a></li>
                                <li><a href="/tools/jpg-to-png" className="text-[hsl(var(--primary))] hover:underline">JPG to PNG</a></li>
                                <li><a href="/blog/how-web-performance-affects-seo" className="text-[hsl(var(--primary))] hover:underline">Guide: Web performance & SEO</a></li>
                            </ul>
                        </Card>
                    </aside>
                </div>

                <ToolLongForm howItWorks={HOW} benefits={BENEFITS} tips={TIPS} />
                <AdSlot placement="tool" />
                <ToolFAQ items={FAQS} />
            </section>
        </>
    );
}

function MetaCell({ label, value }) {
    return (
        <div className="rounded-lg border border-emerald-200/70 bg-white/70 p-3">
            <p className="text-xs text-slate-500">{label}</p>
            <p className="mt-0.5 text-sm font-semibold text-slate-900 tabular-nums">{value}</p>
        </div>
    );
}
